import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ThemeToggle from "@/components/feature/ThemeToggle";
import { useTheme } from "@/theme";
import { site, navigation } from "@/content";

const HOME_PATHS = ["/"];

/**
 * Шапка главной и внутренних страниц.
 *
 * Геометрия (высота, отступы, размеры шрифтов) одинакова в обеих темах —
 * переключатель не должен двигать пункты меню. Отличаются только цвета,
 * они заданы через dark:-варианты.
 */
export default function LightNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark } = useTheme();
  const { pathname } = useLocation();
  const isHome = HOME_PATHS.includes(pathname);

  /** Якоря ведут на главную, если мы не на ней */
  const resolveHref = (href: string) =>
    href.startsWith("#") ? (isHome ? href : `/${href}`) : href;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const navClass = scrolled
    ? "bg-white/90 dark:bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-stone-200/80 dark:border-white/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
    : "bg-[#FAFAF8]/80 dark:bg-[#0A0A0A]/60 backdrop-blur-sm border-b border-transparent";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${navClass}`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          className="flex items-center gap-2 cursor-pointer group shrink-0"
        >
          <div className="w-7 h-7 flex items-center justify-center rounded-md bg-amber-600 dark:bg-amber-500 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 group-hover:bg-amber-500 dark:group-hover:bg-amber-400">
            <i
              className={`${site.brand.icon} text-white dark:text-[#0A0A0A] text-sm transition-transform duration-300 group-hover:scale-110`}
            ></i>
          </div>
          <span className="text-lg font-bold tracking-tight text-gray-900 dark:text-white transition-colors duration-300 group-hover:text-amber-700 dark:group-hover:text-amber-300">
            {site.brand.name}
            <span className="text-amber-600 dark:text-amber-500 transition-colors duration-300 group-hover:text-amber-700 dark:group-hover:text-amber-300">
              {site.brand.suffix}
            </span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-4 xl:gap-6">
          {navigation.main.links.map((link) => (
            <a
              key={link.label}
              href={resolveHref(link.href)}
              className="text-sm font-medium text-stone-600 dark:text-white/60 hover:text-stone-900 dark:hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <a
            href={resolveHref(navigation.main.cta.href)}
            className="text-sm font-bold px-5 py-2.5 rounded-full bg-stone-900 dark:bg-orange-500 text-white dark:text-[#0A0A0A] hover:bg-stone-800 dark:hover:bg-orange-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            {navigation.main.cta.label}
          </a>
        </div>

        <button
          type="button"
          className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? site.a11y.menuClose : site.a11y.menuOpen}
          aria-expanded={menuOpen}
        >
          <i
            className={`${
              menuOpen ? "ri-close-line" : "ri-menu-line"
            } text-2xl text-stone-800 dark:text-white`}
          ></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-stone-100 dark:border-white/[0.06] px-6 py-4 flex flex-col gap-4">
          {navigation.main.links.map((link) => (
            <a
              key={link.label}
              href={resolveHref(link.href)}
              onClick={() => setMenuOpen(false)}
              className="text-stone-700 dark:text-white/70 font-medium text-sm cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <ThemeToggle />
          <a
            href={resolveHref(navigation.main.cta.href)}
            onClick={() => setMenuOpen(false)}
            className="mt-2 text-center text-sm font-bold px-5 py-3 rounded-full bg-stone-900 dark:bg-orange-500 text-white dark:text-[#0A0A0A] cursor-pointer whitespace-nowrap"
          >
            {navigation.main.cta.label}
          </a>
        </div>
      )}
    </nav>
  );
}
